import { format, addYears, addMonths, endOfWeek, startOfWeek, addDays, addWeeks } from 'date-fns';
import React, { useRef, useCallback } from 'react';
import { normalizeArrowKey } from '../../../utils/keyboard/keyboard.esm.js';
import '@popperjs/core';
import { MAX_DATE, MIN_DATE } from '../constants.esm.js';
import { DateGrid } from './DateGrid.esm.js';
import { Header } from './Header.esm.js';
import { useCallbackNextEffect } from './useCallbackNextEffect.esm.js';

/**
 * A calendar for picking dates.
 *
 * The `Calendar` is a lower-level component. For most use cases, prefer the `DatePickerDialog`. Use
 * the `Calendar` directly when you need a different, custom interaction.
 *
 * ### Import Guide
 *
 * ```jsx
 * import { Calendar } from "@panwds/react-ui";
 * ```
 */
function Calendar({ activeDate, dataMetrics, dataTestId, hoverDate, max = MAX_DATE, min = MIN_DATE, onActiveDateChange, onHoverDateChange, onSelectedDateChange, selectedDate, dateRange, weekStartsOn = 0, }) {
    const dateGrid = useRef(null);
    const focusActiveDate = useCallback(() => {
        var _a, _b;
        const date = format(activeDate, "d");
        (_b = (_a = dateGrid.current) === null || _a === void 0 ? void 0 : _a.querySelector(`[data-date="${date}"]`)) === null || _b === void 0 ? void 0 : _b.focus();
    }, [activeDate]);
    const applyFocusNextEffect = useCallbackNextEffect(focusActiveDate);
    // Keyboard navigation as defined by spec:
    // https://www.w3.org/TR/wai-aria-practices/examples/dialog-modal/datepicker-dialog.html
    const handleKeyDown = useCallback((e) => {
        switch (normalizeArrowKey(e)) {
            case "ArrowUp":
                e.preventDefault();
                onActiveDateChange((activeDate) => addWeeks(activeDate, -1));
                applyFocusNextEffect();
                break;
            case "ArrowDown":
                e.preventDefault();
                onActiveDateChange((activeDate) => addWeeks(activeDate, 1));
                applyFocusNextEffect();
                break;
            case "ArrowRight":
                e.preventDefault();
                onActiveDateChange((activeDate) => addDays(activeDate, 1));
                applyFocusNextEffect();
                break;
            case "ArrowLeft":
                e.preventDefault();
                onActiveDateChange((activeDate) => addDays(activeDate, -1));
                applyFocusNextEffect();
                break;
            case "Home":
                e.preventDefault();
                onActiveDateChange((activeDate) => startOfWeek(activeDate, {
                    weekStartsOn,
                }));
                applyFocusNextEffect();
                break;
            case "End":
                e.preventDefault();
                onActiveDateChange((activeDate) => endOfWeek(activeDate, {
                    weekStartsOn,
                }));
                applyFocusNextEffect();
                break;
            /* istanbul ignore next (not supported by user event test library) */
            case "PageUp":
                e.preventDefault();
                if (e.shiftKey) {
                    onActiveDateChange((activeDate) => addYears(activeDate, -1));
                }
                else {
                    onActiveDateChange((activeDate) => addMonths(activeDate, -1));
                }
                applyFocusNextEffect();
                break;
            /* istanbul ignore next (not supported by user event test library) */
            case "PageDown":
                e.preventDefault();
                if (e.shiftKey) {
                    onActiveDateChange((activeDate) => addYears(activeDate, 1));
                }
                else {
                    onActiveDateChange((activeDate) => addMonths(activeDate, 1));
                }
                applyFocusNextEffect();
                break;
        }
    }, []);
    return (React.createElement("div", { "data-metrics": dataMetrics, "data-testid": dataTestId || "calendar", className: "tw-font-lato tw-bg-white tw-flex tw-flex-col tw-space-y-2 dark:tw-bg-gray-900" },
        React.createElement(Header, { activeDate: activeDate, max: max, min: min, onKeyDown: handleKeyDown, onActiveDateChange: onActiveDateChange }),
        React.createElement(DateGrid, { activeDate: activeDate, hoverDate: hoverDate, max: max, min: min, onHoverDateChange: onHoverDateChange, onKeyDown: handleKeyDown, onSelectedDateChange: onSelectedDateChange, ref: dateGrid, selectedDate: selectedDate, dateRange: dateRange, weekStartsOn: weekStartsOn })));
}

export { Calendar };
//# sourceMappingURL=Calendar.esm.js.map
